/**
 * Debug Panel Component
 *
 * Collapsible panel for inspecting Convex payloads, connection status, and event logs.
 * Useful for development debugging.
 */

import React, { useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView, Pressable } from 'react-native'
import { createComponent } from 'react-native-small-ui'
import { useGamePanelsStore } from '../../screens/GameContainer/useGamePanelsStore'

interface DebugPanelProps {
  /**
   * Convex game state data
   */
  gameData?: any

  /**
   * Convex handHistory state data
   */
  handHistoryData?: any

  /**
   * Convex players data
   */
  playersData?: any[]

  /**
   * Connection status
   */
  connectionStatus?: 'connected' | 'connecting' | 'disconnected' | 'error'

  /**
   * Event logs (array of log entries)
   */
  eventLogs?: Array<{
    timestamp: number
    type: string
    message: string
    data?: any
  }>

  /**
   * Initially collapsed
   */
  initiallyCollapsed?: boolean

  /**
   * Maximum height when expanded
   */
  maxHeight?: number
}

/**
 * Format JSON data for display
 */
function formatJSON(data: any): string {
  try {
    return JSON.stringify(data, null, 2)
  } catch (error) {
    return 'Error formatting JSON'
  }
}

/**
 * Get connection status color
 */
function getConnectionColor(status: DebugPanelProps['connectionStatus']): string {
  switch (status) {
    case 'connected':
      return '#10B981' // Green
    case 'connecting':
      return '#F59E0B' // Orange
    case 'disconnected':
      return '#9CA3AF' // Gray
    case 'error':
      return '#EF4444' // Red
    default:
      return '#9CA3AF'
  }
}

/**
 * Debug Panel Component
 */
export function DebugPanel({
  gameData,
  playersData,
  handHistoryData,
  connectionStatus = 'connected',
  eventLogs = [],
  initiallyCollapsed = true,
  maxHeight = 400
}: DebugPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed)
  const [activeTab, setActiveTab] = useState<'game' | 'players' | 'handHistory' | 'logs'>('game')
  const { setDebugPanel } = useGamePanelsStore()

  const connectionColor = getConnectionColor(connectionStatus)

  return (
    <DebugContainer>
      {/* Header (Always Visible) */}
      <HeaderRow>
        <HeaderButton
          onPress={() => setIsCollapsed(!isCollapsed)}
          accessibilityLabel={isCollapsed ? 'Expand debug panel' : 'Collapse debug panel'}
          accessibilityRole="button"
          style={{ flex: 1 }}
        >
          <HeaderLeft>
            <DebugIcon>🐛</DebugIcon>
            <HeaderText>Debug Panel</HeaderText>
          </HeaderLeft>
        </HeaderButton>

        <HeaderRight>
          {/* Connection Status */}
          <ConnectionDot style={{ backgroundColor: connectionColor }} />
          <ConnectionText>{connectionStatus}</ConnectionText>

          {/* Collapse Icon */}
          <TouchableOpacity onPress={() => setIsCollapsed(!isCollapsed)} hitSlop={8}>
            <CollapseIcon>{isCollapsed ? '▼' : '▲'}</CollapseIcon>
          </TouchableOpacity>

          {/* Close Button */}
          <TouchableOpacity onPress={() => setDebugPanel(false)} hitSlop={8}>
            <CloseIcon>✕</CloseIcon>
          </TouchableOpacity>
        </HeaderRight>
      </HeaderRow>

      {/* Expanded Content */}
      {!isCollapsed && (
        <ContentContainer>
          {/* Tabs */}
          <TabsRow>
            <TabButton
              onPress={() => setActiveTab('game')}
              style={{
                backgroundColor: activeTab === 'game' ? 'rgba(139, 92, 246, 0.3)' : 'transparent'
              }}
            >
              <TabText style={{ color: activeTab === 'game' ? '#8B5CF6' : 'rgba(255, 255, 255, 0.7)' }}>Game</TabText>
            </TabButton>

            <TabButton
              onPress={() => setActiveTab('players')}
              style={{
                backgroundColor: activeTab === 'players' ? 'rgba(139, 92, 246, 0.3)' : 'transparent'
              }}
            >
              <TabText style={{ color: activeTab === 'players' ? '#8B5CF6' : 'rgba(255, 255, 255, 0.7)' }}>
                Players
              </TabText>
            </TabButton>

            <TabButton
              onPress={() => setActiveTab('handHistory')}
              style={{
                backgroundColor: activeTab === 'handHistory' ? 'rgba(139, 92, 246, 0.3)' : 'transparent'
              }}
            >
              <TabText style={{ color: activeTab === 'handHistory' ? '#8B5CF6' : 'rgba(255, 255, 255, 0.7)' }}>
                Hand History
              </TabText>
            </TabButton>

            {/* <TabButton
              onPress={() => setActiveTab('logs')}
              style={{
                backgroundColor: activeTab === 'logs' ? 'rgba(139, 92, 246, 0.3)' : 'transparent'
              }}
            >
              <TabText style={{ color: activeTab === 'logs' ? '#8B5CF6' : 'rgba(255, 255, 255, 0.7)' }}>
                Logs ({eventLogs.length})
              </TabText>
            </TabButton> */}
          </TabsRow>

          {/* Content Area */}
          <ScrollView style={{ maxHeight }} showsVerticalScrollIndicator={true}>
            <DataContainer>
              {activeTab === 'game' && (
                <CodeBlock>
                  <CodeText>{formatJSON(gameData || { message: 'No game data' })}</CodeText>
                </CodeBlock>
              )}

              {activeTab === 'players' && (
                <CodeBlock>
                  <CodeText>{formatJSON(playersData || [])}</CodeText>
                </CodeBlock>
              )}

              {activeTab === 'handHistory' && (
                <CodeBlock>
                  <CodeText>{formatJSON(handHistoryData || [])}</CodeText>
                </CodeBlock>
              )}

              {activeTab === 'logs' && (
                <LogsContainer>
                  {eventLogs.length === 0 ? (
                    <EmptyMessage>No event logs</EmptyMessage>
                  ) : (
                    eventLogs.map((log, index) => (
                      <LogEntry key={index}>
                        <LogTimestamp>{new Date(log.timestamp).toLocaleTimeString()}</LogTimestamp>
                        <LogType>{log.type}</LogType>
                        <LogMessage>{log.message}</LogMessage>
                        {log.data && <CodeText>{formatJSON(log.data)}</CodeText>}
                      </LogEntry>
                    ))
                  )}
                </LogsContainer>
              )}
            </DataContainer>
          </ScrollView>

          {/* Footer Info */}
          <FooterInfo>⚠️ Debug panel is for development use only. Disable in production.</FooterInfo>
        </ContentContainer>
      )}
    </DebugContainer>
  )
}

// Styled components
const DebugContainer = createComponent(View, {
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  borderRadius: 12,
  borderWidth: 1,
  borderColor: '#8B5CF6',
  overflow: 'hidden',
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.25,
  shadowRadius: 3.84,
  elevation: 5
})

const HeaderButton = createComponent(TouchableOpacity, {
  padding: 12
})

const HeaderRow = createComponent(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: 6,
  paddingVertical: 4
})

const HeaderLeft = createComponent(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4
})

const DebugIcon = createComponent(Text, {
  fontSize: 8
})

const HeaderText = createComponent(Text, {
  fontSize: 12,
  fontWeight: 'bold',
  color: '#FFFFFF'
})

const HeaderRight = createComponent(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8
})

const ConnectionDot = createComponent(View, {
  width: 8,
  height: 8,
  borderRadius: 4
})

const ConnectionText = createComponent(Text, {
  fontSize: 11,
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'capitalize'
})

const CollapseIcon = createComponent(Text, {
  fontSize: 12,
  color: 'rgba(255, 255, 255, 0.7)'
})

const CloseIcon = createComponent(Text, {
  fontSize: 16,
  color: 'rgba(255, 255, 255, 0.7)',
  fontWeight: '600'
})

const ContentContainer = createComponent(View, {
  borderTopWidth: 1,
  borderTopColor: 'rgba(139, 92, 246, 0.3)'
})

const TabsRow = createComponent(View, {
  flexDirection: 'row',
  gap: 4,
  paddingHorizontal: 12,
  paddingVertical: 8,
  backgroundColor: 'rgba(0, 0, 0, 0.3)'
})

const TabButton = createComponent(Pressable, {
  paddingVertical: 2,
  paddingHorizontal: 6,
  borderRadius: 4,
  alignItems: 'center'
})

const TabText = createComponent(Text, {
  fontSize: 12,
  fontWeight: '600'
})

const DataContainer = createComponent(View, {
  paddingHorizontal: 12
})

const CodeBlock = createComponent(View, {
  backgroundColor: '#1E1E1E',
  borderRadius: 6,
  padding: 10,
  borderWidth: 1,
  borderColor: 'rgba(255, 255, 255, 0.1)'
})

const CodeText = createComponent(Text, {
  fontSize: 11,
  color: '#A8E6CF',
  fontFamily: 'monospace',
  lineHeight: 16
})

const LogsContainer = createComponent(View, {
  gap: 8
})

const LogEntry = createComponent(View, {
  backgroundColor: '#1E1E1E',
  borderRadius: 6,
  padding: 8,
  gap: 4,
  borderLeftWidth: 3,
  borderLeftColor: '#8B5CF6'
})

const LogTimestamp = createComponent(Text, {
  fontSize: 10,
  color: 'rgba(255, 255, 255, 0.5)',
  fontFamily: 'monospace'
})

const LogType = createComponent(Text, {
  fontSize: 11,
  fontWeight: 'bold',
  color: '#8B5CF6',
  textTransform: 'uppercase'
})

const LogMessage = createComponent(Text, {
  fontSize: 12,
  color: '#FFFFFF'
})

const EmptyMessage = createComponent(Text, {
  fontSize: 12,
  color: 'rgba(255, 255, 255, 0.5)',
  textAlign: 'center',
  fontStyle: 'italic',
  paddingVertical: 20
})

const FooterInfo = createComponent(Text, {
  fontSize: 10,
  color: 'rgba(255, 255, 255, 0.5)',
  textAlign: 'center',
  padding: 8,
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  borderTopWidth: 1,
  borderTopColor: 'rgba(139, 92, 246, 0.3)'
})
